<template>
    <section class="evolutions-complement" aria-label="Évolutions après clôture">
        <p v-if="erreur" class="alert alert-danger" role="alert">{{ erreur }}</p>
        <p v-if="chargement" class="text-muted" role="status">Chargement des évolutions…</p>
        <!-- Même carte que celle de l'évaluation initiale : en-tête repliable, fiche et nombre de sections, rubriques repliables. -->
        <div v-for="carte in cartes" :key="carte.cle" class="card brder-radius-x mb-3 dossier-groupe-card">
            <div class="card-body">
                <div class="basic-flex">
                    <a
                        href="javascript:;"
                        :aria-expanded="ouverte === carte.cle"
                        :aria-controls="ouverte === carte.cle ? 'complement-' + carte.cle : null"
                        @click="basculer(carte)">
                        <div class="c-box flx flx-start flx__gap--01 flx-algncenter">
                            <span>
                                <i class="ic-down-arrow ic-primary-svg h-20p"></i>
                            </span>
                            <div class="dossier-groupe-libelle">
                                <h5 class="text-primary font-22">{{ carte.groupe }}</h5>
                                <small>{{ carte.evolution_id ? 'Complément après la clôture' : 'Complément après la clôture · créé au premier enregistrement' }}</small>
                            </div>
                        </div>
                    </a>
                    <div class="dossier-groupe-meta">
                        <span v-if="fiche" class="dossier-fiche-nom" :title="fiche">
                            <span>{{ fiche }}</span>
                            <small>{{ rubriques(carte).length }} sections</small>
                        </span>
                        <button
                            type="button"
                            class="complement-renommer"
                            :disabled="carte.enregistrement"
                            @click="editerTitre(carte)">
                            <i class="fas fa-pen" aria-hidden="true"></i>Renommer
                        </button>
                    </div>
                </div>
                <form v-if="carte.edition" class="complement-titre" @submit.prevent="renommer(carte)">
                    <label class="sr-only" :for="'titre-evolution-' + carte.cle">Titre de l’évolution</label>
                    <input
                        :id="'titre-evolution-' + carte.cle"
                        v-model="carte.titre"
                        class="form-control"
                        maxlength="190"
                        :disabled="carte.enregistrement" />
                    <button type="submit" class="btn btn-primary rounded-pill" :disabled="carte.enregistrement || !carte.titre.trim()">Enregistrer</button>
                    <button type="button" class="btn btn-outline-secondary rounded-pill" :disabled="carte.enregistrement" @click="annulerTitre(carte)">Annuler</button>
                </form>
                <p v-if="carte.erreur" class="alert alert-danger mt-3 mb-0" role="alert">{{ carte.erreur }}</p>

                <div v-if="ouverte === carte.cle" :id="'complement-' + carte.cle" class="w-100 mt-4 box-drop section-box show">
                    <div
                        v-for="section in rubriques(carte)"
                        :key="section.id"
                        class="mb-4 mt-3 dossier-section-repliable"
                        :class="{ 'dossier-section-repliee': estRepliee(carte, section) }">
                        <button
                            type="button"
                            class="dossier-section-chevron"
                            :aria-label="(estRepliee(carte, section) ? 'Déployer ' : 'Replier ') + section.libelle"
                            :aria-expanded="!estRepliee(carte, section)"
                            @click="basculerSection(carte, section)">
                            <i class="fas fa-chevron-down" aria-hidden="true"></i>
                        </button>
                        <template v-if="rubriqueNative(section)">
                            <component
                                :is="composantNatif(section)"
                                v-if="carte.evolution_id > 0 && composantNatif(section)"
                                v-bind="propsRubrique(carte, section)"
                                @nouvelle-prise="$emit('nouvelle-prise')"
                                @mesures-enregistrees="$emit('mesures-enregistrees')"
                                @emitViewOnGoingAnalyseResult="$emit('emitViewOnGoingAnalyseResult', $event)"
                                @emitViewOnGoingExamenParaResult="$emit('emitViewOnGoingExamenParaResult', $event)" />
                            <div v-else class="dossier-section-attente">
                                <h3>{{ section.libelle }}</h3>
                                <button
                                    v-if="composantNatif(section)"
                                    type="button"
                                    :disabled="carte.enregistrement"
                                    @click="commencer(carte)">
                                    Commencer
                                </button>
                                <span v-else>Rubrique indisponible</span>
                            </div>
                        </template>
                        <ConsultationSections
                            v-else
                            v-bind="propsRubrique(carte, section)"
                            :enregistrer_rubrique="donnees => enregistrerRubrique(carte, donnees)" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { composantNatif } from './rubriques-natives'
import groupesApi from '@/api/consultations/_consultation-group-sections'
import ficheApi from '@/store/mods/consultations/fiche-consultations'
import ConsultationSections from '@/views/consultations/en-cours/consultation-sections'

function messageErreur(error) {
    return error && error.response && error.response.data && error.response.data.message || error.message || 'Connexion impossible.'
}

export default {
    components: { ConsultationSections },
    props: { consultation: Object, sections: Array, fiche: String, utilisateur: Object, patient: Object, info_assurance_patient: Object },
    data() {
        // Cartes repliées à l'ouverture du dossier ; rubriques dépliées, comme dans l'évaluation initiale.
        return { cartes: [], groupes: [], ouverte: 0, repliees: {}, sequence: 0, chargement: true, erreur: '', quitte: false }
    },
    computed: {
        sectionsDisponibles() {
            const sections = this.sections.slice()
            this.groupes.forEach(groupe => (groupe.sections || []).forEach(section => {
                if (!sections.some(item => Number(item.id) === Number(section.id))) sections.push(section)
            }))
            return sections
        }
    },
    mounted() { this.charger() },
    beforeDestroy() { this.quitte = true },
    methods: {
        composantNatif,
        rubriqueNative(section) {
            return Number(section.has_field) === 0 || Number(section.is_certif_type) === 1 || section.slug === 'dhis2_diagnostics'
        },
        propsRubrique(carte, section) {
            return {
                consultation: this.consultation, section_name: section.libelle, slug: section.slug,
                fcs_id: Number(section.fcs_id || 0), evolution_id: carte.evolution_id || -carte.cle,
                current_consultation_evolution: carte.evolution_id || -carte.cle, evolution_toggler: 0,
                is_start: 1, activeRole: 'ROLE_MEDECIN', active_role: 'ROLE_MEDECIN', mode_infirmier: false,
                user_info: this.utilisateur, patient: this.patient, info_assurance_patient: this.info_assurance_patient || {}
            }
        },
        async commencer(carte) {
            if (carte.evolution_id || carte.enregistrement || this.quitte) return
            carte.enregistrement = true
            carte.erreur = ''
            try { await this.enregistrerGroupe(carte) }
            catch (error) { carte.erreur = messageErreur(error) }
            finally { carte.enregistrement = false }
        },
        carte(groupe) {
            // Mêmes rubriques que l'évaluation initiale, sauf si l'évolution a enregistré les siennes.
            const sections = groupe.sections && groupe.sections.length ? groupe.sections : this.sections
            return {
                cle: ++this.sequence, evolution_id: Number(groupe.groupe_id || 0), groupe: groupe.groupe,
                titre: groupe.groupe, sections: sections.map(section => Number(section.id)),
                erreur: '', enregistrement: false, edition: false
            }
        },
        basculer(carte) { this.ouverte = this.ouverte === carte.cle ? 0 : carte.cle },
        estRepliee(carte, section) { return Boolean(this.repliees[carte.cle + ':' + section.id]) },
        basculerSection(carte, section) {
            const cle = carte.cle + ':' + section.id
            this.$set(this.repliees, cle, !this.repliees[cle])
        },
        editerTitre(carte) {
            carte.titre = carte.groupe
            carte.erreur = ''
            carte.edition = true
        },
        annulerTitre(carte) {
            carte.titre = carte.groupe
            carte.erreur = ''
            carte.edition = false
        },
        async charger() {
            try {
                this.groupes = await groupesApi.getListeConsultationGroupSection(this.consultation.id)
                this.cartes = this.groupes.filter(groupe => groupe.apres_cloture === true).map(this.carte)
                if (!this.cartes.length) this.cartes.push(this.carte({ groupe: this.titreLibre() }))
            } catch (error) { this.erreur = messageErreur(error) }
            finally { this.chargement = false }
        },
        titreLibre() {
            const titres = this.groupes.concat(this.cartes).map(groupe => groupe.groupe)
            let titre = 'Évolution', numero = 2
            while (titres.includes(titre)) titre = 'Évolution ' + numero++
            return titre
        },
        ajouterEvolution() {
            if (this.erreur) return
            const carte = this.carte({ groupe: this.titreLibre() })
            this.cartes.push(carte)
            this.ouverte = carte.cle
        },
        rubriques(carte) { return this.sectionsDisponibles.filter(section => carte.sections.includes(Number(section.id))) },
        async enregistrerGroupe(carte) {
            const resultat = await groupesApi.saveConsultationEvolution({
                consultation_id: this.consultation.id, evolution_id: carte.evolution_id,
                titre: carte.titre, sections: carte.sections, user_id: this.utilisateur.id
            })
            if (!resultat.success) throw new Error(resultat.message)
            if (!carte.evolution_id) {
                if (!(Number(resultat.evolution_id) > 0)) throw new Error('Identifiant d’évolution absent de la réponse.')
                carte.evolution_id = Number(resultat.evolution_id)
            }
            carte.groupe = carte.titre
        },
        async renommer(carte) {
            carte.erreur = ''
            carte.enregistrement = true
            try {
                if (carte.evolution_id) await this.enregistrerGroupe(carte)
                else carte.groupe = carte.titre
                carte.edition = false
            } catch (error) { carte.erreur = messageErreur(error) }
            finally { carte.enregistrement = false }
        },
        async enregistrerRubrique(carte, donnees) {
            const consultationId = this.consultation.id
            if (this.quitte) throw new Error('Le dossier a été quitté.')
            if (carte.enregistrement) throw new Error('Un enregistrement est déjà en cours dans cette évolution.')
            carte.enregistrement = true
            try {
                if (!carte.evolution_id) await this.enregistrerGroupe(carte)
                if (this.quitte || this.consultation.id !== consultationId) throw new Error('Le dossier a été quitté.')
                donnees.set('evolution_id', carte.evolution_id)
                // Conserver group_id : 0 à la création, identifiant de rubrique à la modification.
                const resultat = await ficheApi.saveCustomSectionData(donnees)
                await this.$nextTick()
                return resultat
            } finally { carte.enregistrement = false }
        }
    }
}
</script>
<style scoped lang="scss">
.complement-renommer {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid #c9d7f2;
    border-radius: 999px;
    background: #fff;
    color: var(--ui-accent, #2457c5);
    font-size: 12.5px;
    font-weight: 600;
    &:disabled { opacity: .6; }
    &:focus-visible { outline: 2px solid var(--ui-accent, #2457c5); outline-offset: 2px; }
}
.complement-titre {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
    .form-control { flex: 1 1 260px; max-width: 520px; }
}
</style>
